import type React from 'react';
import { useMemo } from 'react';
import type { Calendar } from '@roger/ui-components';
import type { EpisodeLupaPeriodResponse } from '../services/api';
import type { TaskListResponse } from '../services/api/types.gen';
import { parseISOToLocalDate } from '../utils/dateUtils';
import {
  isInPdgmPeriod,
  isPdgmPeriodBoundaryStart,
  PDGM_OUTSIDE_EPISODE_DAY_BG,
  PDGM_PERIOD_1_DAY_BG,
  resolvePdgmPeriodBoundaries,
  type PdgmPeriodBoundaries,
} from '../utils/pdgmPeriodUtils';
import {
  CALENDAR_EXTERNAL_CHARTING_DAY_CLASS,
  CHARTS_FROM_BOUNDARY_DAY_CLASS,
  episodeShowsChartsFromCutoff,
  isBeforeRogerChartsFromDay,
  isRogerChartsFromDay,
} from './patient/chartsFromEpisodeUi';

type CalendarProps = React.ComponentProps<typeof Calendar>;

export const PDGM_PERIOD_BOUNDARY_DAY_CLASS =
  "relative before:pointer-events-none before:absolute before:left-0 before:top-1/2 before:z-30 before:h-[85%] before:w-[3px] before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:bg-gray-900 before:content-['']";

/** Legend swatches; the day styles below add only the cell rounding. */
export const SCHEDULED_VISIT_SWATCH_STYLE: React.CSSProperties = {
  backgroundColor: 'rgb(219 234 254)',
  outline: '2px dashed rgb(147 197 253)',
  outlineOffset: '-2px',
};
export const COMPLETED_VISIT_SWATCH_STYLE: React.CSSProperties = {
  backgroundColor: 'rgb(191 219 254)',
  outline: '2px solid rgb(96 165 250)',
  outlineOffset: '-2px',
};
export const TODAY_SWATCH_STYLE: React.CSSProperties = {
  boxShadow: 'inset 0 0 0 2px rgb(79 70 229)',
};

/** Days holding saved visits, keyed by `Date.toDateString()`. */
export interface VisitDayKinds {
  /** Every visit on the day is completed or exported. */
  completed: Set<string>;
  /** At least one visit on the day is unfinished. */
  pending: Set<string>;
}

/**
 * Visits by plotted day (`task_date`, else `scheduled_date`) inside
 * `[from, to]`, each day's list ordered by date then id.
 */
export const groupVisitsByDate = (
  visits: TaskListResponse[],
  from: Date,
  to: Date
): Map<string, TaskListResponse[]> => {
  const grouped = new Map<string, TaskListResponse[]>();
  for (const visit of visits) {
    const visitDate = parseISOToLocalDate(
      visit.task_date ?? visit.scheduled_date
    );
    if (!visitDate || visitDate < from || visitDate > to) continue;
    const dateKey = visitDate.toDateString();
    if (!grouped.has(dateKey)) grouped.set(dateKey, []);
    grouped.get(dateKey)!.push(visit);
  }
  for (const dayVisits of grouped.values()) {
    dayVisits.sort((left, right) => {
      const leftDate = left.task_date ?? left.scheduled_date;
      const rightDate = right.task_date ?? right.scheduled_date;
      return (
        leftDate.localeCompare(rightDate) || left.id.localeCompare(right.id)
      );
    });
  }
  return grouped;
};

/** Separate completed days (all visits done) from pending days (any unfinished). */
export const visitDayKinds = (
  visitsByDate: Map<string, TaskListResponse[]>
): VisitDayKinds => {
  const completed = new Set<string>();
  const pending = new Set<string>();
  visitsByDate.forEach((dayVisits, dateKey) => {
    if (
      dayVisits.every(v => v.status === 'completed' || v.status === 'exported')
    )
      completed.add(dateKey);
    else pending.add(dateKey);
  });
  return { completed, pending };
};

/** The episode's first and last day, or null when either is missing. */
export const parseEpisodeDateRange = (
  episodeStartDate?: string | null,
  episodeEndDate?: string | null
): { start: Date; end: Date } | null => {
  if (episodeStartDate && episodeEndDate) {
    const start = parseISOToLocalDate(episodeStartDate);
    const end = parseISOToLocalDate(episodeEndDate);
    if (start && end) return { start, end };
  }
  return null;
};

interface EpisodeCalendarMarkersArgs {
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /** Day Roger charts this episode from; markers when it straddles. */
  rogerChartsFrom?: string | null;
  lupaPeriods?: EpisodeLupaPeriodResponse[];
  visitDays: VisitDayKinds;
}

/**
 * The episode calendar's day markers: outside-episode wash, 1st 30-day period
 * wash and boundary line, External EMR charting, today, and saved visits.
 * Shared by the Tasks calendar and Schedule Visits so the two read the same.
 */
export const useEpisodeCalendarMarkers = ({
  episodeStartDate,
  episodeEndDate,
  rogerChartsFrom,
  lupaPeriods,
  visitDays,
}: EpisodeCalendarMarkersArgs) => {
  const episodeDateRange = useMemo(
    () => parseEpisodeDateRange(episodeStartDate, episodeEndDate),
    [episodeStartDate, episodeEndDate]
  );

  const pdgmBoundaries = useMemo(
    (): PdgmPeriodBoundaries | null =>
      resolvePdgmPeriodBoundaries(
        lupaPeriods,
        episodeDateRange?.start ?? null,
        episodeDateRange?.end ?? null,
        parseISOToLocalDate
      ),
    [lupaPeriods, episodeDateRange]
  );

  const hasTwoPdgmPeriods = pdgmBoundaries?.periodCount === 2;

  const isPdgmPeriodBoundaryDay = useMemo(() => {
    if (!hasTwoPdgmPeriods || !pdgmBoundaries) {
      return undefined;
    }
    const boundaries = pdgmBoundaries;
    return (date: Date) => isPdgmPeriodBoundaryStart(date, boundaries);
  }, [hasTwoPdgmPeriods, pdgmBoundaries]);

  const isPdgmPeriod1Day = useMemo(() => {
    if (!hasTwoPdgmPeriods || !pdgmBoundaries || !episodeDateRange) {
      return undefined;
    }
    const boundaries = pdgmBoundaries;
    const { start, end } = episodeDateRange;
    return (date: Date) => isInPdgmPeriod(date, 1, boundaries, start, end);
  }, [hasTwoPdgmPeriods, pdgmBoundaries, episodeDateRange]);

  const showChartsFromCutoff = useMemo(
    () =>
      episodeShowsChartsFromCutoff(
        rogerChartsFrom && episodeStartDate && episodeEndDate
          ? {
              start_date: episodeStartDate,
              end_date: episodeEndDate,
              roger_charts_from: rogerChartsFrom,
            }
          : null
      ),
    [rogerChartsFrom, episodeStartDate, episodeEndDate]
  );

  const isBeforeChartsFromDay = useMemo(() => {
    if (!showChartsFromCutoff || !rogerChartsFrom || !episodeDateRange) {
      return undefined;
    }
    const chartsFrom = rogerChartsFrom;
    const { start, end } = episodeDateRange;
    return (date: Date) =>
      date >= start &&
      date <= end &&
      isBeforeRogerChartsFromDay(date, chartsFrom);
  }, [showChartsFromCutoff, rogerChartsFrom, episodeDateRange]);

  const isChartsFromBoundaryDay = useMemo(() => {
    if (!showChartsFromCutoff || !rogerChartsFrom) {
      return undefined;
    }
    const chartsFrom = rogerChartsFrom;
    return (date: Date) => isRogerChartsFromDay(date, chartsFrom);
  }, [showChartsFromCutoff, rogerChartsFrom]);

  const calendarProps = useMemo(() => {
    const classNames: CalendarProps['classNames'] = {
      // Remove built-in bg-accent on today so our modifiersStyles control the background
      today: 'rounded-md',
      // Collapse week gutters so period-1 day washes abut into one block
      // instead of a grid of individually rounded tiles.
      ...(hasTwoPdgmPeriods && {
        week: 'mt-0 flex w-full',
      }),
    };
    const modifiers: NonNullable<CalendarProps['modifiers']> = {
      notEpisodeDay: episodeDateRange
        ? (date: Date) =>
            date < episodeDateRange.start || date > episodeDateRange.end
        : () => false,
      episodeDay: episodeDateRange
        ? (date: Date) =>
            date >= episodeDateRange.start && date <= episodeDateRange.end
        : () => true,
      ...(isPdgmPeriod1Day && {
        pdgmPeriod1: isPdgmPeriod1Day,
      }),
      hasPendingVisits: (date: Date) =>
        visitDays.pending.has(date.toDateString()),
      hasCompletedVisits: (date: Date) =>
        visitDays.completed.has(date.toDateString()),
      ...(isPdgmPeriodBoundaryDay && {
        pdgmPeriodBoundary: isPdgmPeriodBoundaryDay,
      }),
      ...(isBeforeChartsFromDay && {
        beforeChartsFrom: isBeforeChartsFromDay,
      }),
      ...(isChartsFromBoundaryDay && {
        chartsFromBoundary: isChartsFromBoundaryDay,
      }),
    };
    const modifiersClassNames: NonNullable<
      CalendarProps['modifiersClassNames']
    > = {
      ...(isPdgmPeriod1Day && {
        // Square cells so adjacent period-1 days read as one wash;
        // visit/today modifiers keep their own rounding on top.
        pdgmPeriod1: 'rounded-none [&_button]:rounded-none',
      }),
      ...(isPdgmPeriodBoundaryDay && {
        pdgmPeriodBoundary: PDGM_PERIOD_BOUNDARY_DAY_CLASS,
      }),
      ...(isChartsFromBoundaryDay && {
        chartsFromBoundary: CHARTS_FROM_BOUNDARY_DAY_CLASS,
      }),
      ...(isBeforeChartsFromDay && {
        beforeChartsFrom: CALENDAR_EXTERNAL_CHARTING_DAY_CLASS,
      }),
    };
    const modifiersStyles: NonNullable<CalendarProps['modifiersStyles']> = {
      notEpisodeDay: {
        backgroundColor: PDGM_OUTSIDE_EPISODE_DAY_BG,
        backgroundImage: 'none',
        color: 'rgb(156 163 175)',
      },
      // Period-1 wash before visit fills so pending/completed blues win.
      ...(isPdgmPeriod1Day && {
        pdgmPeriod1: {
          backgroundColor: PDGM_PERIOD_1_DAY_BG,
          borderRadius: 0,
        },
      }),
      today: {
        ...TODAY_SWATCH_STYLE,
        borderRadius: '6px',
        fontWeight: '600',
      },
      hasPendingVisits: {
        ...SCHEDULED_VISIT_SWATCH_STYLE,
        borderRadius: '4px',
      },
      hasCompletedVisits: {
        ...COMPLETED_VISIT_SWATCH_STYLE,
        borderRadius: '4px',
      },
    };
    return { classNames, modifiers, modifiersClassNames, modifiersStyles };
  }, [
    hasTwoPdgmPeriods,
    episodeDateRange,
    isPdgmPeriod1Day,
    isPdgmPeriodBoundaryDay,
    isBeforeChartsFromDay,
    isChartsFromBoundaryDay,
    visitDays,
  ]);

  return {
    hasTwoPdgmPeriods,
    showChartsFromCutoff,
    ...calendarProps,
  };
};
